import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'core/utilities/uuid';

import { Input, Icon, FloatGroup, useLocalStorage } from 'modules/common';
import DonationAmount from 'modules/pages/common/donations/donation-amount.component';
import DonationAmounts from 'modules/pages/common/donations/donation-amounts.component';

const eventName = 'page_amounts';

function UpsellDonations({
  editedUpsell,
  onUpdateEditedUpsell,
  maxNumberOfItems,
  subtitle,
  noCard,
  isMultiplier = false,
  allowDescriptions = false,
}) {
  const [descriptionsToggledOn, setDescriptionsToggledOn] = useLocalStorage('upsellDonationDescriptions', false);

  // keep track of dynamic vs static amounts shown on the ui
  const amounts = isMultiplier
    ? editedUpsell.page_amounts.filter(amt => amt._isMultiplier)
    : editedUpsell.page_amounts.filter(amt => !amt._isMultiplier);

  /* An upsell that was saved with labels has to open with them visible no matter what the stored
     toggle says. Turning the toggle off clears the labels, so this stays in sync with the cog. */
  const hasAmountLabels = amounts.some(amt => !amt._destroy && !amt.destroy && !!amt.description);
  const showDescriptions = descriptionsToggledOn || hasAmountLabels;

  const clearDescriptions = () => {
    const clearedAmounts = editedUpsell.page_amounts.map(amt => (amt.description ? { ...amt, description: '' } : amt));
    if (clearedAmounts.every((amt, index) => amt === editedUpsell.page_amounts[index])) return;

    onUpdateEditedUpsell({ target: { name: eventName, value: clearedAmounts } });
  };

  const toggleDescriptions = () => {
    const nextShowDescriptions = !showDescriptions;
    /* The donation card renders labels whether or not the inputs are visible, so hiding them
       has to drop the values too rather than leaving them to show up on the card. */
    if (!nextShowDescriptions) clearDescriptions();

    setDescriptionsToggledOn(nextShowDescriptions);
  };

  const amountNotDuplicated = donation => {
    const matchingAmounts = editedUpsell.page_amounts.filter(d => {
      /* Amounts that have been deleted shouldn't be considered. */
      if (d._destroy || d.destroy) return false;

      // eslint-disable-next-line eqeqeq
      return d.amount == donation && d._isMultiplier === isMultiplier;
    });

    return matchingAmounts.length <= 1;
  };

  const onlyOnePreselected = () => {
    const numPreselected = editedUpsell.page_amounts.reduce((onlyOne, d) => {
      // Amounts that have been deleted shouldn't be considered.
      if (d._destroy || d.destroy) return onlyOne;
      // If this donation isn't of the type of the slate upsell, ignore it.
      if (d._isMultiplier !== isMultiplier) return onlyOne;

      if (d.preselected) onlyOne += 1;
      return onlyOne;
    }, 0);
    // Only one amount may be preselected.
    return numPreselected <= 1;
  };

  const createNewDonation = () => {
    return { amount: '', description: '', _isMultiplier: isMultiplier, id: uuid(), preselected: false };
  };

  return (
    <>
      {allowDescriptions && (
        <div className="d-flex justify-content-end mb-2">
          <div className="pointer" role="button" onClick={toggleDescriptions}>
            <Icon icon={showDescriptions ? 'cog-solid' : 'cog'} />
          </div>
        </div>
      )}
      <DonationAmounts
        eventName={eventName}
        amounts={editedUpsell.page_amounts}
        updateAmounts={onUpdateEditedUpsell}
        newDonation={createNewDonation}
        maxNumberOfItems={maxNumberOfItems}
        subtitle="Customize your suggested donation amount buttons."
        noCard={noCard}
        isMultiplier={isMultiplier}
        disabled={editedUpsell._isDisabled}
      >
        {({ updateItem, removeItem }) => {
          return amounts.map(donation => {
            if (donation._destroy) return null;

            const amountField = (
              <DonationAmount
                donation={donation}
                updateAmount={updateItem}
                removeAmount={removeItem}
                disabled={editedUpsell._isDisabled}
                value={donation.amount}
                isMultiplier={isMultiplier}
                validators={['required', amountNotDuplicated]}
                errorMessages={[
                  'The donation amount is required.',
                  isMultiplier ? 'This donation multiplier already exists.' : 'This donation amount already exists.',
                ]}
              />
            );

            if (!allowDescriptions || !showDescriptions) {
              return <React.Fragment key={donation.id || donation.position}>{amountField}</React.Fragment>;
            }

            return (
              <div className="d-flex flex-row w-100" key={donation.id || donation.position}>
                {amountField}
                <FloatGroup className="mt-1 ml-2 w-100">
                  <Input
                    type="text"
                    name="description"
                    disabled={editedUpsell._isDisabled}
                    value={donation.description || ''}
                    placeholder="Amount Label"
                    onChange={event => updateItem(event, donation)}
                  />
                </FloatGroup>
              </div>
            );
          });
        }}
      </DonationAmounts>
      {/* Validate that only one amount is preselected */}
      <Input
        type="hidden"
        validators={[onlyOnePreselected]}
        errorMessages={['Only one amount can be preselected']}
        errorMessageClasses="text-center"
      />
    </>
  );
}

UpsellDonations.propTypes = {
  editedUpsell: PropTypes.object.isRequired,
  onUpdateEditedUpsell: PropTypes.func.isRequired,
  maxNumberOfItems: PropTypes.number,
  subtitle: PropTypes.string,
  noCard: PropTypes.bool,
  isMultiplier: PropTypes.bool,
  allowDescriptions: PropTypes.bool,
};

UpsellDonations.defaultProps = {
  maxNumberOfItems: 9999,
};

export default UpsellDonations;
